import React from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Container, Typography, Paper, Breadcrumbs, Link as MuiLink, Divider, Stack, Button,
} from '@mui/material';
import { ArrowBack, Security, Lock, Visibility, Gavel, ContactSupport } from '@mui/icons-material';
import { C } from './vps/theme';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ backgroundColor: C.bgSection, minHeight: '100vh', py: { xs: 5, md: 8 } }}>
      <Container maxWidth="lg">
        {/* Navigation / Header */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/')}
            sx={{
              color: C.primary,
              fontWeight: 600,
              mb: 2,
              '&:hover': { backgroundColor: C.primaryLight },
            }}
          >
            Back to Home
          </Button>

          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <MuiLink component={RouterLink} to="/" underline="hover" color="inherit" sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
              Home
            </MuiLink>
            <Typography color="text.primary" sx={{ fontSize: '0.9rem', fontWeight: 700, color: C.primary }}>
              Privacy Policy
            </Typography>
          </Breadcrumbs>

          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              fontWeight: 800,
              color: C.textPrimary,
              mb: 1,
              letterSpacing: '-0.02em',
            }}
          >
            Hosting Services Privacy Policy
          </Typography>
          <Typography sx={{ color: C.textSecondary, fontSize: '1rem', maxWidth: 720 }}>
            Last updated: January 2026. This policy explains how Hosting Services collects, uses, and safeguards your server data and payment information via Cashfree.com Payment Gateway.
          </Typography>
        </Box>

        {/* Content Card */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 5, md: 6 },
            backgroundColor: '#FFFFFF',
            border: `1px solid ${C.border}`,
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.03)',
          }}
        >
          <Stack spacing={4}>
            {/* Section 1 */}
            <Box>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                <Security sx={{ color: C.primary, fontSize: 26 }} />
                <Typography variant="h5" sx={{ fontWeight: 800, color: C.textPrimary }}>
                  1. Information We Collect
                </Typography>
              </Stack>
              <Typography sx={{ color: C.textSecondary, lineHeight: 1.75, mb: 1.5 }}>
                When you order a VPS server or register an account on Hosting Services, we collect essential information required to provision and maintain your cloud infrastructure:
              </Typography>
              <Typography component="div" sx={{ color: C.textSecondary, lineHeight: 1.75, pl: 2 }}>
                • <strong>Account Details:</strong> Your full name, business name, email address, and phone number.<br />
                • <strong>Server Specifications:</strong> Hostname, chosen operating system (e.g. AlmaLinux, Ubuntu), and server configuration notes.<br />
                • <strong>Payment Information:</strong> Transactions are processed securely via <strong>Cashfree.com Payment Gateway</strong>. We never store raw credit card numbers or UPI PINs on our servers.
              </Typography>
            </Box>

            <Divider sx={{ borderColor: C.border }} />

            {/* Section 2 */}
            <Box>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                <Visibility sx={{ color: C.primary, fontSize: 26 }} />
                <Typography variant="h5" sx={{ fontWeight: 800, color: C.textPrimary }}>
                  2. How We Use Your Information
                </Typography>
              </Stack>
              <Typography sx={{ color: C.textSecondary, lineHeight: 1.75, mb: 1.5 }}>
                We use the collected information exclusively to fulfill hosting operations, deliver customer support, and communicate important system updates:
              </Typography>
              <Typography component="div" sx={{ color: C.textSecondary, lineHeight: 1.75, pl: 2 }}>
                • Instant provisioning of your virtual private server and SSH root access credentials.<br />
                • Issuing official tax invoices and transaction receipts via Cashfree Payment Gateway.<br />
                • Sending critical security notifications, maintenance alerts, and uptime reports.<br />
                • Assisting your technical team with server migration and support requests.
              </Typography>
            </Box>

            <Divider sx={{ borderColor: C.border }} />

            {/* Section 3 */}
            <Box>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                <Lock sx={{ color: C.primary, fontSize: 26 }} />
                <Typography variant="h5" sx={{ fontWeight: 800, color: C.textPrimary }}>
                  3. Data Security & Storage Confidentiality
                </Typography>
              </Stack>
              <Typography sx={{ color: C.textSecondary, lineHeight: 1.75 }}>
                We enforce strict data isolation across our virtual private server infrastructure. Your virtual machine, database contents, and deployment files remain 100% confidential. All administrative connections and billing portals use 256-bit SSL encryption.
              </Typography>
            </Box>

            <Divider sx={{ borderColor: C.border }} />

            {/* Section 4 */}
            <Box>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                <Gavel sx={{ color: C.primary, fontSize: 26 }} />
                <Typography variant="h5" sx={{ fontWeight: 800, color: C.textPrimary }}>
                  4. Third-Party Services
                </Typography>
              </Stack>
              <Typography sx={{ color: C.textSecondary, lineHeight: 1.75 }}>
                We do not sell, rent, or trade your personal data. We integrate with trusted third-party providers — such as <strong>Cashfree.com Payment Gateway</strong> for payment processing — exclusively to execute transactions. Cashfree adheres strictly to PCI DSS Level 1 compliance standards.
              </Typography>
            </Box>

            {/* <Divider sx={{ borderColor: C.border }} /> */}

            {/* Section 5 */}
            {/* <Box>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                <ContactSupport sx={{ color: C.primary, fontSize: 26 }} />
                <Typography variant="h5" sx={{ fontWeight: 800, color: C.textPrimary }}>
                  5. Contact Privacy Team
                </Typography>
              </Stack>
              <Typography sx={{ color: C.textSecondary, lineHeight: 1.75, mb: 2 }}>
                If you have questions regarding this Privacy Policy or wish to update your stored account details, please reach out to our privacy compliance desk:
              </Typography>
              <Box
                sx={{
                  p: 2.5,
                  backgroundColor: C.bgSection,
                  borderRadius: '12px',
                  border: `1px solid ${C.border}`,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700, color: C.textPrimary }}>
                  Hosting Services Privacy Desk
                </Typography>
                <Typography variant="body2" sx={{ color: C.textSecondary, mt: 0.5 }}>
                  Email: support@hostingservices.com | Available 24/7
                </Typography>
              </Box>
            </Box> */}
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;
